export function formatCurrency(value) {
  const inr = Math.ceil(Math.max(0, Number(value) || 0));
  return `₹${inr.toLocaleString('en-IN')}`;
}

export function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('en-IN');
}

export function parseAmount(value) {
  if (value == null || value === '') return 0;
  const n = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

const startOfTodayMs = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const endOfTodayMs = () => startOfTodayMs() + 24 * 60 * 60 * 1000;

export function isTodayDate(raw) {
  if (raw == null || raw === '') return false;
  const trimmed = String(raw).trim();
  let d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) {
    d = new Date(`${trimmed}T12:00:00`);
  }
  if (Number.isNaN(d.getTime())) return false;
  const t = d.getTime();
  return t >= startOfTodayMs() && t < endOfTodayMs();
}

export function isFlightOrBusCancelled(status) {
  if (status === 'TICKET CANCELLED') return true;
  return String(status ?? '').toUpperCase().includes('CANCEL');
}

export function isHotelCancelled(status) {
  return String(status ?? '').toUpperCase().includes('CANCEL');
}

export function isHolidayCancelled(status) {
  return String(status ?? '').toUpperCase().includes('CANCEL');
}

export function isTrainCancelled(item) {
  if (String(item?.cancellationStatus ?? '').toUpperCase() === 'CANCELLATION_SUCCESS') {
    return true;
  }
  return String(item?.status ?? '').toUpperCase().includes('CANCEL');
}

function embarkBalanceFromNode(node) {
  if (!node || typeof node !== 'object') {
    return { wallet: null, credit: null };
  }
  let wallet = null;
  let credit = null;
  if (node.Balance != null && node.Balance !== '') wallet = parseAmount(node.Balance);
  else if (node.balance != null && node.balance !== '') wallet = parseAmount(node.balance);
  if (node.CreditLimit != null && node.CreditLimit !== '') credit = parseAmount(node.CreditLimit);
  else if (node.creditLimit != null && node.creditLimit !== '') credit = parseAmount(node.creditLimit);

  const walletFallback = ['CashBalance', 'cashBalance', 'WalletBalance', 'walletBalance', 'BalanceAmount', 'balanceAmount'];
  const creditFallback = ['CreditBalance', 'creditBalance', 'AvailableCredit', 'availableCredit'];
  walletFallback.forEach((k) => {
    if (wallet == null && node[k] != null && node[k] !== '') wallet = parseAmount(node[k]);
  });
  creditFallback.forEach((k) => {
    if (credit == null && node[k] != null && node[k] !== '') credit = parseAmount(node[k]);
  });
  return { wallet, credit };
}

function embarkErrorIndicatesFailure(err) {
  if (!err || typeof err !== 'object') return false;
  const code = err.ErrorCode ?? err.errorCode;
  if (code == null || code === '') return false;
  if (code === 0 || code === '0') return false;
  const n = Number(code);
  if (Number.isFinite(n) && n === 0) return false;
  if (typeof code === 'string' && !Number.isFinite(n)) return true;
  return Number.isFinite(n) ? n !== 0 : true;
}

/** Extract Embark Balance response fields (wallet = Balance, credit = CreditLimit). */
export function parseEmbarkBalanceFields(payload) {
  if (!payload || typeof payload !== 'object') {
    return { wallet: null, credit: null };
  }
  if (payload.success === false && payload.balance == null && payload.creditLimit == null) {
    return { wallet: null, credit: null };
  }
  if (payload.bus || payload.flight) {
    return embarkBusBalancesOnly(payload);
  }
  const err = payload.Error || payload.error;
  if (embarkErrorIndicatesFailure(err)) {
    return { wallet: null, credit: null };
  }
  const top = embarkBalanceFromNode(payload);
  const nested = payload.Response || payload.response;
  const inner = embarkBalanceFromNode(nested);
  return {
    wallet: top.wallet ?? inner.wallet,
    credit: top.credit ?? inner.credit,
  };
}

/** Bus + flight Embark Balance often query the same agency wallet (same credentials). */
function combineEmbarkProductAmounts(busAmount, flightAmount) {
  const bus = busAmount != null && busAmount !== '' ? parseAmount(busAmount) : null;
  const flight = flightAmount != null && flightAmount !== '' ? parseAmount(flightAmount) : null;
  if (bus == null && flight == null) {
    return { value: null, has: false };
  }
  if (bus == null) {
    return { value: flight, has: true };
  }
  if (flight == null) {
    return { value: bus, has: true };
  }
  if (Math.abs(bus - flight) < 0.01) {
    return { value: bus, has: true };
  }
  return { value: bus + flight, has: true };
}

/** Admin embark-balances: use bus Embark wallet only (do not add flight). */
export function embarkBusBalancesOnly(data) {
  if (!data || typeof data !== 'object' || !data.bus) {
    return { wallet: null, credit: null };
  }
  const { bus } = data;
  const wallet =
    bus.balance != null && bus.balance !== '' ? parseAmount(bus.balance) : null;
  const credit =
    bus.creditLimit != null && bus.creditLimit !== ''
      ? parseAmount(bus.creditLimit)
      : null;
  return { wallet, credit };
}

/** Sum structured admin embark-balances response for dashboard tiles. */
export function combineAdminEmbarkBalances(data) {
  if (!data || typeof data !== 'object') {
    return { wallet: null, credit: null };
  }
  const walletPart = combineEmbarkProductAmounts(data.bus?.balance, data.flight?.balance);
  const creditPart = combineEmbarkProductAmounts(data.bus?.creditLimit, data.flight?.creditLimit);
  return {
    wallet: walletPart.has ? walletPart.value : null,
    credit: creditPart.has ? creditPart.value : null,
  };
}

/** Sum bus + flight Embark Balance / CreditLimit for dashboard tiles. */
export function combineEmbarkApiBalances(...payloads) {
  const parsed = payloads.map((payload) => parseEmbarkBalanceFields(payload));
  let wallet = null;
  let credit = null;
  if (parsed.length >= 2) {
    const walletPart = combineEmbarkProductAmounts(parsed[0].wallet, parsed[1].wallet);
    const creditPart = combineEmbarkProductAmounts(parsed[0].credit, parsed[1].credit);
    wallet = walletPart.has ? walletPart.value : null;
    credit = creditPart.has ? creditPart.value : null;
    for (let i = 2; i < parsed.length; i += 1) {
      if (parsed[i].wallet != null) {
        wallet = (wallet ?? 0) + parsed[i].wallet;
      }
      if (parsed[i].credit != null) {
        credit = (credit ?? 0) + parsed[i].credit;
      }
    }
  } else if (parsed.length === 1) {
    wallet = parsed[0].wallet;
    credit = parsed[0].credit;
  }
  return { wallet, credit };
}
